import { useAdminUserSession } from '@AppModels/hooks/adminStateHooks';
import { Button } from '@HoudiniUI/Button';
import { THEME } from '@Theme';
import { IAccount, ICampaign } from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import { Provider } from 'mobx-react';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { useHistory, useParams } from 'react-router-dom';
import { IImpersonationContext } from '../../../extViewmodels';
import { ILocationState } from '../../../models';
import { useFullscreenModal } from '../../../models/hooks/appStateHooks';
import { ADMIN_ACCOUNT_QUERY_KEY } from '../../../queries/Admin/Account/useAdminAccountQuery';
import { AccountOperationsViewModel, AdminPermission } from '../../../viewmodels/AdminViewModels';
import { BlogReportingTable } from '../../../web/components/blog/BlogReportingTable';
import { ExternalIntegrations } from '../../../web/components/integrations/ExternalIntegrations';
import { AdminSocialMediaReportingTable } from '../../../web/components/socialMedia/SocialMediaReportingTable/AdminSocialReportingTable';
import { PortalDestination } from '../../../web/components/Portal';
import { ClipboardIcon2 } from '../../../web/components/svgs/icons/ClipboardIcon';
import { MenuIcon } from '../../../web/components/svgs/icons/MenuIcon';
import { ScheduleMeetingIcon } from '../../../web/components/svgs/icons/ScheduleMeetingIcon';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { SquareLinkIcon } from '../../../web/components/svgs/icons/SquareLinkIcon';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../../web/components/Tabs';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../web/components-v2/Collapsible';
import { TabToggle } from '../../../web/components-v2/TabToggle';
import {
	AdminDrawerToolbar,
	AppDrawerToolbarButton,
	SideDrawer,
	StickyAppDrawerContent,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { Input } from '../../../web/components-v2/Input';
import { AdminAccountDetailsTags } from '../../../web/containers/PeopleV2/TagsContainer';
import {
	AdminAppAssistantChat,
	SuccessMeetingPrepAppAssistantChat,
	SurveyBuilderAppAssistantChat,
} from '../../../web/containers/templates/AssistantChat';
import { DisabledTabContent } from '../../components/accountDetails/AccountDetailsTabView/presentation';
import { ConditionalAccess } from '../../components/ConditionalAccess';
import { InternalOnly } from '../../components/InternalOnly';
import { isResellerSession } from '../../contexts/ResellerContext';
import { AdminDashboardPageLayout } from '../../containers/AdminDashboardPageLayout';
import { AccountDetails } from '../../containers/account/AccountDetails';
import { AccountAutomationsTab } from '../../containers/account/accountTabs/AccountAutomationsTab';
import { AdminIntegrations } from '../../containers/account/accountTabs/AdminIntegrations';
import { AdminLeadParsers } from '../../containers/account/accountTabs/AdminLeadParsers';
import { AccountEventLogTab } from '../../containers/account/accountTabs/EventLog';
import { ImageLibraryTab } from '../../containers/account/accountTabs/ImageLibraryTab';
import { GroupEmails } from '../../containers/account/accountTabs/GroupEmails';
import { Imports } from '../../containers/account/accountTabs/Imports';
import { Settings } from '../../containers/account/accountTabs/Settings';
import { TextingManagement } from '../../containers/account/accountTabs/TextingManagement';
import { TextingRegistration } from '../../containers/account/accountTabs/TextingRegistration';
import { Users } from '../../containers/account/accountTabs/Users';
import { AccountDetailsTabContentOverview } from './AccountDetailsTabContentOverview';
import {
	AccountSupportTicketsDrawerButton,
	AccountSupportTicketsPanel,
	SUPPORT_TICKETS_DRAWER_VALUE,
} from './AccountSupportTicketsPanel';
import { AddNoteQuickAction } from './AddNoteQuickAction';
import { contentStylesForTab, styleSheet } from './styles';
import { useAccountDetailsData } from './useAccountDetailsData';

export const canShowAccountDetailWebsite = (account: IAccount) =>
	account.features?.websites?.enabled || account.features?.websites?.cancelledDate;
export const canShowAccountDetailContentPreferences = (account: IAccount) =>
	account.features?.customContentCalendar?.enabled;
export const canShowAccountDetailAds = (account: IAccount) => account.features?.ads?.enabled;
export const canShowAccountDetailBlogs = (account: IAccount) => account.features?.blogFeature?.enabled;
export const canShowAccountDetailTexting = (account: IAccount) => account.features?.texting?.enabled;

const ACCOUNT_TABS = [
	{ label: 'Overview', value: 'overview' },
	{ label: 'Website', value: 'website' },
	{ label: 'Custom Content', value: 'content-preferences' },
	{ label: 'Ads', value: 'ads' },
	{ label: 'Blogs', value: 'blogs' },
	{ label: 'Users', value: 'users' },
	{ label: 'Imports', value: 'imports' },
	{ label: 'Texting', value: 'texting' },
	{ label: 'Tags', value: 'tags' },
	{ label: 'Email', value: 'group-emails' },
	{ label: 'Social', value: 'social' },
	{ label: 'Images', value: 'images' },
	{ label: 'Automations', value: 'automations' },
	{ label: 'Integrations', value: 'integrations' },
	{ label: 'Lead Parsers', value: 'lead-parsers' },
	{ label: 'Event Log', value: 'event-log' },
	{ label: 'Settings', value: 'settings' },
] as const;

type TabValue = (typeof ACCOUNT_TABS)[number]['value'];

export const ACCOUNT_DETAILS_ROUTE = '/accounts/:accountId/:tab?/:subView?';

// Overview owns its own per-card Edit, so the page-level pencil would be a second, competing affordance there.
const EDITABLE_TABS = new Set<TabValue>(['website', 'content-preferences', 'ads', 'settings']);

const TEXTING_SUB_VIEW_ITEMS = [
	{ label: 'Phone Numbers', value: 'numbers' },
	{ label: 'Registration', value: 'registration' },
];

// The sub-view rides the URL — compliance notification emails deep-link straight to
// /accounts/{accountId}/texting/registration, which must render on a cold load.
export const TextingTabContent = ({ account }: { account: IAccount }) => {
	const history = useHistory();
	const params = useParams<{ subView?: string }>();
	const userSession = useAdminUserSession();
	// Registration is internal-only; a reseller deep-linking to it falls back to Phone Numbers.
	const showRegistration = !isResellerSession(userSession);
	const subView = showRegistration && params.subView === 'registration' ? 'registration' : 'numbers';
	const onSubViewChange = (value: string) => {
		const basePath = `/accounts/${account.id}/texting`;
		history.push(value === 'registration' ? `${basePath}/registration` : basePath);
	};
	return (
		<>
			<TabToggle
				items={
					showRegistration
						? TEXTING_SUB_VIEW_ITEMS
						: TEXTING_SUB_VIEW_ITEMS.filter(item => item.value !== 'registration')
				}
				size='small'
				value={subView}
				onValueChange={onSubViewChange}
				styles={[styleSheet.textingSubViewToggle]}
			/>
			{subView === 'registration' ? <TextingRegistration account={account} /> : <TextingManagement account={account} />}
		</>
	);
};

const AccountDetailsPageContent: React.FC = () => {
	const params = useParams<{ accountId: string; tab?: string }>();
	const history = useHistory();
	const fullscreenModal = useFullscreenModal();
	const queryClient = useQueryClient();
	const userSession = useAdminUserSession();
	// Two gates, preserved from the header actions this panel replaced: filing a ticket was open to
	// curated support agents, while the support-desk deep link stayed developer-only.
	const hasSupportDeskAccess = userSession.hasPermission(AdminPermission.OnlyForDevelopers);
	const hasSupportPanelAccess = !!userSession.canAccessSupport || hasSupportDeskAccess;
	const { account, accountOperations, impersonationContextVM } = useAccountDetailsData(params.accountId);
	const impersonationContext: IImpersonationContext = { account: { id: params.accountId } };

	const [isEditMode, setIsEditMode] = React.useState(false);
	const [consumedUsers, setConsumedUsers] = React.useState<number | null>(null);
	const [integrationsSearchQuery, setIntegrationsSearchQuery] = React.useState('');
	const [isMobileNavOpen, setIsMobileNavOpen] = React.useState(false);
	const [overviewRefreshKey, setOverviewRefreshKey] = React.useState(0);
	// The Email and Social tabs are each their own scroll container, so each needs its own
	// scroll-to-bottom anchor for the reporting tables' infinite-scroll Waypoint to portal into.
	const emailReportScrollPortalId = React.useId();
	const socialReportScrollPortalId = React.useId();
	const isCompactLayout = useMediaQuery({ maxWidth: THEME.breakpoints.lg });
	React.useLayoutEffect(() => {
		if (!isCompactLayout) setIsMobileNavOpen(false);
	}, [isCompactLayout]);

	React.useEffect(() => {
		if (accountOperations) {
			const userCountPromise = accountOperations.getUserCount();
			if (userCountPromise) {
				userCountPromise
					.then(count => setConsumedUsers(count))
					.catch(() => {
						// Silently fail - will show just licensed count
					});
			}
		}
	}, [accountOperations]);

	// Reset edit mode when the tab changes so a tab/card left mid-edit doesn't bleed edit chrome
	// into the next tab (externalEditMode would otherwise stay true across the switch).
	React.useEffect(() => {
		setIsEditMode(false);
	}, [params.tab]);

	const getUsersTabLabel = () => {
		const totalLicenses = account?.planDetails?.users;
		if (totalLicenses == null) {
			return 'Users';
		}
		if (consumedUsers != null) {
			return `Users\u00A0(${consumedUsers}/${totalLicenses})`;
		}
		return `Users\u00A0(${totalLicenses})`;
	};

	const validTabValues = new Set<string>(ACCOUNT_TABS.map(t => t.value));
	const selectedTab: TabValue = (validTabValues.has(params.tab) ? params.tab : 'overview') as TabValue;
	const currentTabLabel = ACCOUNT_TABS.find(t => t.value === selectedTab)?.label ?? 'Menu';

	const onTabChange = (value: string) => {
		setIsMobileNavOpen(false);
		if (value === 'content-calendar') {
			return;
		}
		const basePath = `/accounts/${params.accountId}`;
		if (value === 'overview') {
			history.push(basePath);
		} else {
			history.push(`${basePath}/${value}`);
		}
	};

	const onScheduleEmailsForClientClicked = React.useCallback(() => {
		const locationState: ILocationState<AccountOperationsViewModel, unknown> = {
			viewModel: accountOperations,
		};
		fullscreenModal.history.push({
			pathname: '/email/bulk-email-approval',
			state: locationState,
		});
	}, [accountOperations, fullscreenModal]);

	const onContentCalendarClicked = React.useCallback(() => {
		const { origin, pathname } = window.location;
		window.open(`${origin}${pathname}#/accounts/${params.accountId}/content-calendar`, '_blank');
	}, [params.accountId]);

	const onAccountViewBulkEmailAttempts = React.useCallback(() => {
		history.push(`/email/account-bulk-historical-report/${params.accountId}`);
	}, [history, params.accountId]);

	const onCampaignViewBulkEmailAttempts = React.useCallback(
		(campaign: ICampaign) => {
			fullscreenModal.history.push(`/email/campaign-bulk-historical-report/${params.accountId}/${campaign.id}`);
		},
		[fullscreenModal, params.accountId]
	);

	const [lastSavedAccount, setLastSavedAccount] = React.useState<IAccount | undefined>(undefined);
	React.useEffect(() => {
		setLastSavedAccount(undefined);
	}, [params.accountId]);

	const onAccountUpdated = React.useCallback(
		(updatedAccount: IAccount) => {
			queryClient.setQueryData([ADMIN_ACCOUNT_QUERY_KEY, params.accountId], updatedAccount);
			setLastSavedAccount(updatedAccount);
		},
		[queryClient, params.accountId]
	);

	const grantTemporaryAccess = React.useCallback(async () => {
		const updatedAccount = await accountOperations.grantTemporaryAccess();
		onAccountUpdated(updatedAccount);
	}, [accountOperations, onAccountUpdated]);

	const onMakeMainContact = React.useCallback(
		async (contactId: string) => {
			if (!account) return;
			const updatedAccount = await accountOperations.saveAccount({
				...account,
				additionalInfo: {
					...account.additionalInfo,
					mainContactUserId: contactId || '',
				},
			});
			if (updatedAccount) {
				onAccountUpdated(updatedAccount);
			}
		},
		[account, accountOperations, onAccountUpdated]
	);

	const renderNotAuthorized = () => (
		<div className={css(styleSheet.unauthorized)}>
			<div>Sorry, you&apos;re not authorized</div>
			<div style={{ fontSize: 50 }}>😿</div>
		</div>
	);
	const tabTriggers = (
		<>
			<TabTrigger value='overview' title='Overview'>
				Overview
			</TabTrigger>
			<TabTrigger value='content-calendar' onClick={onContentCalendarClicked}>
				Calendar
				<SquareLinkIcon />
			</TabTrigger>
			{canShowAccountDetailWebsite(account) ? (
				<TabTrigger value='website' title='Website'>
					Website
				</TabTrigger>
			) : null}
			{canShowAccountDetailContentPreferences(account) ? (
				<TabTrigger value='content-preferences'>Custom Content</TabTrigger>
			) : null}
			{canShowAccountDetailAds(account) ? <TabTrigger value='ads'>Ads</TabTrigger> : null}
			{canShowAccountDetailBlogs(account) ? <TabTrigger value='blogs'>Blogs</TabTrigger> : null}
			<TabTrigger value='users'>{getUsersTabLabel()}</TabTrigger>
			<TabTrigger value='imports'>Imports</TabTrigger>
			<TabTrigger value='texting'>Texting</TabTrigger>
			<TabTrigger value='tags'>Tags</TabTrigger>
			<TabTrigger value='group-emails'>Email</TabTrigger>
			<TabTrigger value='social'>Social</TabTrigger>
			<TabTrigger value='images'>Images</TabTrigger>
			<TabTrigger value='automations'>Automations</TabTrigger>
			<TabTrigger value='integrations'>Integrations</TabTrigger>
			<TabTrigger value='lead-parsers'>Lead Parsers</TabTrigger>
			<InternalOnly>
				<TabTrigger value='event-log'>Event Log</TabTrigger>
			</InternalOnly>
			<TabTrigger value='settings'>Settings</TabTrigger>
		</>
	);

	return (
		<Provider impersonationContext={impersonationContextVM}>
			<AdminDashboardPageLayout
				compactNav
				contentStyles={contentStylesForTab(selectedTab)}
				sidebarChildren={
					<SideDrawer>
						<StickyAppDrawerContent value='ai-assistant'>
							<AdminAppAssistantChat impersonationContext={impersonationContext} />
						</StickyAppDrawerContent>
						<StickyAppDrawerContent value='success-meeting-prep'>
							<SuccessMeetingPrepAppAssistantChat impersonationContext={impersonationContext} />
						</StickyAppDrawerContent>
						<ConditionalAccess permission={AdminPermission.ModifyAccount}>
							{account.features?.surveys?.enabled ? (
								<StickyAppDrawerContent value='survey-builder'>
									<SurveyBuilderAppAssistantChat impersonationContext={impersonationContext} />
								</StickyAppDrawerContent>
							) : null}
						</ConditionalAccess>
						{hasSupportPanelAccess ? (
							<StickyAppDrawerContent value={SUPPORT_TICKETS_DRAWER_VALUE}>
								<AccountSupportTicketsPanel
									accountId={params.accountId}
									accountName={account?.companyName}
									canOpenSupportDesk={hasSupportDeskAccess}
								/>
							</StickyAppDrawerContent>
						) : null}
						<AdminDrawerToolbar>
							<AppDrawerToolbarButton value='ai-assistant' tooltip='AI Assistant'>
								<SparklesIcon />
							</AppDrawerToolbarButton>
							<ConditionalAccess permission={AdminPermission.ModifyAccount}>
								<AppDrawerToolbarButton value='success-meeting-prep' tooltip='Prepare for Meeting'>
									<ScheduleMeetingIcon fill='currentColor' />
								</AppDrawerToolbarButton>
							</ConditionalAccess>
							<ConditionalAccess permission={AdminPermission.ModifyAccount}>
								{account.features?.surveys?.enabled ? (
									<AppDrawerToolbarButton value='survey-builder' tooltip='Survey Assistant'>
										<ClipboardIcon2 />
									</AppDrawerToolbarButton>
								) : null}
							</ConditionalAccess>
							{hasSupportPanelAccess ? <AccountSupportTicketsDrawerButton accountId={params.accountId} /> : null}
						</AdminDrawerToolbar>
					</SideDrawer>
				}
			>
				<div className={css(styleSheet.accountDetailsHeader)}>
					<AccountDetails
						hideEditButton={!EDITABLE_TABS.has(selectedTab)}
						externalEditMode={isEditMode}
						onEditModeChange={setIsEditMode}
						onAccountUpdated={onAccountUpdated}
						lastSavedAccount={lastSavedAccount}
						extraActionEntities={
							<AddNoteQuickAction
								accountOperations={accountOperations}
								onAccountUpdated={onAccountUpdated}
								onNoteSaved={() => setOverviewRefreshKey(key => key + 1)}
							/>
						}
					/>
				</div>
				<TabRoot
					value={selectedTab}
					onValueChange={onTabChange}
					orientation='vertical'
					className={css(styleSheet.container)}
				>
					{isCompactLayout ? (
						<Collapsible open={isMobileNavOpen} onOpenChange={setIsMobileNavOpen} className={css(styleSheet.mobileNav)}>
							<CollapsibleTrigger
								render={
									<Button variant='ghost' styles={[styleSheet.mobileNavTrigger]}>
										<MenuIcon fillColor='currentColor' />
										{currentTabLabel}
									</Button>
								}
							/>
							<CollapsiblePanel>
								<TabList styles={[styleSheet.mobileNavList]}>{tabTriggers}</TabList>
							</CollapsiblePanel>
						</Collapsible>
					) : (
						<TabList styles={[styleSheet.tabList]}>{tabTriggers}</TabList>
					)}
					<TabContent value='overview' className={css(styleSheet.tabContent)}>
						<AccountDetailsTabContentOverview
							key={overviewRefreshKey}
							onEditModeChange={setIsEditMode}
							onAccountUpdated={onAccountUpdated}
						/>
					</TabContent>
					{canShowAccountDetailWebsite(account) ? (
						<TabContent value='website' className={css(styleSheet.tabContent)}>
							<AccountDetails
								hideHeader
								showOnlyWebsiteModule
								externalEditMode={isEditMode}
								onEditModeChange={setIsEditMode}
								onAccountUpdated={onAccountUpdated}
								lastSavedAccount={lastSavedAccount}
							/>
						</TabContent>
					) : null}
					{canShowAccountDetailContentPreferences(account) ? (
						<TabContent value='content-preferences' className={css(styleSheet.tabContent)}>
							<AccountDetails
								hideHeader
								showOnlyCustomContentModule
								externalEditMode={isEditMode}
								onEditModeChange={setIsEditMode}
								onAccountUpdated={onAccountUpdated}
								lastSavedAccount={lastSavedAccount}
							/>
						</TabContent>
					) : null}
					{canShowAccountDetailAds(account) ? (
						<TabContent value='ads' className={css(styleSheet.tabContent)}>
							<AccountDetails
								hideHeader
								showOnlyAdsModule
								externalEditMode={isEditMode}
								onEditModeChange={setIsEditMode}
								onAccountUpdated={onAccountUpdated}
								lastSavedAccount={lastSavedAccount}
							/>
						</TabContent>
					) : null}
					{canShowAccountDetailBlogs(account) ? (
						<TabContent value='blogs' className={css(styleSheet.selfScrollingTabContent)}>
							<BlogReportingTable canOpenPosts={false} fillsContainer impersonationContext={{ account }} />
						</TabContent>
					) : null}
					<TabContent value='users' className={css(styleSheet.tabContent)}>
						<Users
							account={account}
							grantTemporaryAccess={grantTemporaryAccess}
							onMakeMainContact={onMakeMainContact}
						/>
					</TabContent>
					<TabContent value='imports' className={css(styleSheet.tabContent)}>
						<Imports accountVM={accountOperations} />
					</TabContent>
					<TabContent value='texting' className={css(styleSheet.tabContent)}>
						{canShowAccountDetailTexting(account) ? (
							<TextingTabContent account={account} />
						) : (
							<DisabledTabContent>Texting is not enabled for this account.</DisabledTabContent>
						)}
					</TabContent>
					<TabContent value='tags' className={css(styleSheet.tagsTabContent)}>
						<AdminAccountDetailsTags impersonationContext={{ account }} />
					</TabContent>
					<TabContent value='group-emails' className={css(styleSheet.tabContent)}>
						<ConditionalAccess
							elseChildren={renderNotAuthorized()}
							or={AdminPermission.ReadCampaigns}
							permission={AdminPermission.CreateCampaigns}
						>
							<GroupEmails
								accountId={account.id}
								onScheduleEmailsForClientClicked={onScheduleEmailsForClientClicked}
								onContentCalendarClicked={onContentCalendarClicked}
								onAccountViewBulkEmailAttempts={onAccountViewBulkEmailAttempts}
								onCampaignViewBulkEmailAttempts={onCampaignViewBulkEmailAttempts}
								scrollToBottomPortalId={emailReportScrollPortalId}
							/>
						</ConditionalAccess>
						<PortalDestination id={emailReportScrollPortalId} />
					</TabContent>
					<TabContent value='images' className={css(styleSheet.tabContent)}>
						<ImageLibraryTab impersonationContext={impersonationContext} />
					</TabContent>
					<TabContent value='automations' className={css(styleSheet.tabContent)}>
						<ConditionalAccess
							elseChildren={renderNotAuthorized()}
							permission={AdminPermission.CreateAutomationTemplateOnBehalf}
						>
							{account?.features?.automation?.enabled ? (
								<AccountAutomationsTab account={account} />
							) : (
								<DisabledTabContent>Automations are disabled for this account.</DisabledTabContent>
							)}
						</ConditionalAccess>
					</TabContent>
					<TabContent value='social' className={css(styleSheet.tabContent)}>
						<ConditionalAccess
							elseChildren={renderNotAuthorized()}
							or={AdminPermission.ReadCampaigns}
							permission={AdminPermission.CreateCampaigns}
						>
							<AdminSocialMediaReportingTable
								impersonationContext={{ account }}
								scrollToBottomPortalId={socialReportScrollPortalId}
							/>
						</ConditionalAccess>
						<PortalDestination id={socialReportScrollPortalId} />
					</TabContent>
					<TabContent value='integrations' className={css(styleSheet.tabContent)}>
						<Input
							styles={[styleSheet.integrationsSearchInput]}
							placeholder='Search integrations...'
							type='text'
							value={integrationsSearchQuery}
							onChange={e => setIntegrationsSearchQuery(e.target.value)}
						/>
						<h3>Integrations (Current)</h3>
						<ExternalIntegrations impersonationContext={{ account }} searchQuery={integrationsSearchQuery} />
						<h3>Integrations (Legacy)</h3>
						<AdminIntegrations
							accountVM={accountOperations}
							adminUserSession={userSession}
							searchQuery={integrationsSearchQuery}
						/>
					</TabContent>
					<TabContent value='lead-parsers' className={css(styleSheet.tabContent)}>
						<AdminLeadParsers accountVM={accountOperations} adminUserSession={userSession} />
					</TabContent>
					<TabContent value='event-log' className={css(styleSheet.tabContent)}>
						<InternalOnly>
							<ConditionalAccess permission={AdminPermission.ReadAccount}>
								<AccountEventLogTab accountId={account.id} />
							</ConditionalAccess>
						</InternalOnly>
					</TabContent>
					<TabContent value='settings' className={css(styleSheet.tabContent)}>
						<AccountDetails
							hideHeader
							externalEditMode={isEditMode}
							onEditModeChange={setIsEditMode}
							onAccountUpdated={onAccountUpdated}
							lastSavedAccount={lastSavedAccount}
						>
							<Settings scrollToBottomPortalId='' />
						</AccountDetails>
					</TabContent>
				</TabRoot>
			</AdminDashboardPageLayout>
		</Provider>
	);
};

// Remount on account switch so useAccountDetailsData's useState-snapshotted viewmodels rebuild for the new account.
export const AdminAccountDetailsPage: React.FC = () => {
	const { accountId } = useParams<{ accountId: string }>();
	return <AccountDetailsPageContent key={accountId} />;
};
